import {
  MonacoLanguageType,
  filePathExtension,
  filePathStem,
  getFileSizeBytes,
  guardNever,
  normalizeFilePathExtension,
} from "@hex/common";

import { ContextWorkbenchEntity } from "../contextWorkbenchEntity";
import { ContextWorkbenchFile } from "../contextWorkbenchFile";

import { getEntityRefForEntity } from "./entityRef";
import { getFileIdForEntityRef } from "./fileId";

export function getFileForEntity(
  entity: ContextWorkbenchEntity,
): ContextWorkbenchFile.Entity {
  if (entity.type === ContextWorkbenchEntity.Type.MagicRulesFile) {
    return getFileForMagicRulesEntity(entity);
  } else if (entity.type === ContextWorkbenchEntity.Type.OrgGuideFile) {
    return getFileForOrgGuideFileEntity(entity);
  } else {
    guardNever(entity, entity);
    return null as never;
  }
}

function getFileForMagicRulesEntity(
  entity: ContextWorkbenchEntity.MagicRulesFile,
): ContextWorkbenchFile.Entity {
  const entityRef = getEntityRefForEntity(entity);
  const id = getFileIdForEntityRef(entityRef);
  const filepath = entity.filename;
  const stem = filePathStem(filepath);
  const extension = filePathExtension(filepath);
  const language = getFileLanguage(extension);
  const contents = entity.fileContent;
  const sizeBytes = getFileSizeBytes(contents);
  return {
    id,
    type: ContextWorkbenchFile.Type.Entity,
    entityRef,
    contents,
    filepath,
    stem,
    extension,
    language,
    sizeBytes,
    readOnly: true,
  };
}

function getFileForOrgGuideFileEntity(
  entity: ContextWorkbenchEntity.OrgGuideFile,
): ContextWorkbenchFile.Entity {
  const entityRef = getEntityRefForEntity(entity);
  const id = getFileIdForEntityRef(entityRef);
  const filepath = entity.filePath;
  const stem = filePathStem(filepath);
  const extension = filePathExtension(filepath);
  const language = getFileLanguage(extension);
  const contents = entity.contents;
  const sizeBytes = getFileSizeBytes(contents);
  return {
    id,
    type: ContextWorkbenchFile.Type.Entity,
    entityRef,
    contents,
    filepath,
    stem,
    extension,
    language,
    sizeBytes,
    readOnly: entity.externalSource != null,
  };
}

function getFileLanguage(extension: string): MonacoLanguageType {
  const normalizedExtension = normalizeFilePathExtension(extension);
  switch (normalizedExtension) {
    case ".sql":
      return "sql";
    case ".json":
      return "json";
    case ".md":
      return "markdown";
    case ".txt":
    default:
      return "plaintext";
  }
}

export function fileComparator(
  a: ContextWorkbenchFile,
  b: ContextWorkbenchFile,
): number {
  return a.filepath.localeCompare(b.filepath);
}
